<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto bg-taupe-50"
    >
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-6 sm:pt-8 lg:pt-10 xl:max-w-7xl xl:mx-auto w-full"
        >
            <h1 id="primary-heading" class="sr-only">Admin: Team Details</h1>
            <div class="px-4 sm:px-6 lg:px-8 pb-12 space-y-8">
                <!-- Header with tabs -->
                <div class="mb-6">
                    <admin-header-tabs current="teams" />
                </div>

                <zen-loading-indicator v-if="loading" />

                <template v-if="!loading && team">
                    <!-- Team name -->
                    <div class="mb-6">
                        <div
                            class="flex items-center justify-between min-h-[2.5rem]"
                        >
                            <h2 class="text-2xl font-medium text-taupe-900">
                                {{ team.name }}
                            </h2>
                            <div class="flex items-center gap-1">
                                <button
                                    v-if="can(PERMISSIONS.TEAM_UPDATE)"
                                    @click="openEditModal"
                                    class="cursor-pointer p-2 text-taupe-400 hover:text-taupe-600 transition-colors"
                                    title="Edit team"
                                    data-testid="edit-team-button"
                                >
                                    <svg
                                        class="h-5 w-5"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="1.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    >
                                        <path
                                            d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                                        />
                                    </svg>
                                </button>
                                <zen-button
                                    :to="{ name: 'AdminTeamsScreen' }"
                                    variant="tertiary"
                                    size="small"
                                    data-testid="back-to-teams"
                                >
                                    <template #icon>
                                        <svg
                                            class="h-4 w-4"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fill-rule="evenodd"
                                                d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z"
                                                clip-rule="evenodd"
                                            />
                                        </svg>
                                    </template>
                                    All Teams
                                </zen-button>
                            </div>
                        </div>
                        <p
                            v-if="team.description"
                            class="mt-1 text-sm text-taupe-500"
                        >
                            {{ team.description }}
                        </p>
                    </div>

                    <!-- Two column layout -->
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <!-- Members Section -->
                        <div
                            class="bg-white shadow-sm rounded-lg overflow-hidden"
                        >
                            <div
                                class="px-6 py-4 border-b border-taupe-200 flex items-center justify-between"
                            >
                                <h3
                                    class="text-base font-medium text-taupe-900"
                                >
                                    Members ({{ members.length }})
                                </h3>
                            </div>
                            <div class="px-6 py-4">
                                <!-- Add Member -->
                                <div
                                    v-if="canManageMembers"
                                    class="flex gap-2 mb-4"
                                    data-testid="add-member-controls"
                                >
                                    <zen-auto-complete
                                        class="flex-1"
                                        v-model="selectedUserUlid"
                                        input-id="add-member"
                                        :search="searchAvailableUsers"
                                        option-label="label"
                                        option-value="ulid"
                                        placeholder="Select a user to add..."
                                        show-dropdown-button
                                        :min-length="0"
                                    />
                                    <zen-button
                                        variant="secondary"
                                        size="medium"
                                        :disabled="!selectedUserUlid"
                                        :processing="addingUser"
                                        data-testid="add-member-button"
                                        @click="addMember()"
                                    >
                                        Add
                                    </zen-button>
                                </div>

                                <!-- Member List -->
                                <ul
                                    v-if="members.length > 0"
                                    class="divide-y divide-taupe-200 border border-taupe-200 rounded-md"
                                >
                                    <li
                                        v-for="member in members"
                                        :key="member.ulid"
                                        class="flex items-center justify-between px-4 py-3"
                                    >
                                        <div class="flex items-center gap-3">
                                            <div
                                                class="h-8 w-8 rounded-full bg-sky-100 flex items-center justify-center text-sm font-medium text-sky-700"
                                            >
                                                {{
                                                    (member.first_name?.[0] ||
                                                        '') +
                                                    (member.last_name?.[0] ||
                                                        '')
                                                }}
                                            </div>
                                            <div>
                                                <p
                                                    class="text-sm font-medium text-taupe-900"
                                                >
                                                    {{ member.display_name }}
                                                </p>
                                                <p
                                                    class="text-xs text-taupe-500"
                                                >
                                                    {{ member.email }}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            v-if="canManageMembers"
                                            @click="removeMember(member.ulid)"
                                            class="text-taupe-400 hover:text-red-500 transition-colors"
                                            :title="
                                                'Remove ' + member.display_name
                                            "
                                            :data-testid="
                                                'remove-member-' + member.ulid
                                            "
                                        >
                                            <svg
                                                class="h-5 w-5"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                            >
                                                <path
                                                    fill-rule="evenodd"
                                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                    clip-rule="evenodd"
                                                />
                                            </svg>
                                        </button>
                                    </li>
                                </ul>

                                <zen-empty-table-message
                                    v-if="members.length === 0"
                                    compact
                                    icon="ZenIconUsers"
                                >
                                    No members in this team yet.
                                </zen-empty-table-message>
                            </div>
                        </div>

                        <!-- Permissions Section -->
                        <div
                            class="bg-white shadow-sm rounded-lg overflow-hidden"
                        >
                            <div
                                class="px-6 py-4 border-b border-taupe-200 flex items-center justify-between"
                            >
                                <h3
                                    class="text-base font-medium text-taupe-900"
                                >
                                    Permissions ({{ grants.length }})
                                </h3>
                                <zen-button
                                    v-if="isAdmin"
                                    variant="secondary"
                                    size="small"
                                    @click="openGrantModal()"
                                >
                                    Add Permission
                                </zen-button>
                            </div>
                            <div class="px-6 py-4">
                                <!-- Grants List -->
                                <ul
                                    v-if="grants.length > 0"
                                    class="divide-y divide-taupe-200 border border-taupe-200 rounded-md"
                                >
                                    <li
                                        v-for="grant in grants"
                                        :key="grant.ulid"
                                        class="flex items-center justify-between px-4 py-3"
                                        :data-testid="'grant-' + grant.ulid"
                                    >
                                        <div>
                                            <div
                                                class="flex items-center gap-2"
                                            >
                                                <zen-inline-pill
                                                    :value="grant.role"
                                                />
                                                <span
                                                    class="text-sm text-taupe-500"
                                                >
                                                    at
                                                </span>
                                                <span
                                                    class="text-sm font-medium text-taupe-900"
                                                >
                                                    {{ scopeLabel(grant) }}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            v-if="isAdmin"
                                            @click="revokeGrantConfirm(grant)"
                                            class="text-taupe-400 hover:text-red-500 transition-colors"
                                            title="Revoke permission"
                                        >
                                            <svg
                                                class="h-5 w-5"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                            >
                                                <path
                                                    fill-rule="evenodd"
                                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                    clip-rule="evenodd"
                                                />
                                            </svg>
                                        </button>
                                    </li>
                                </ul>

                                <zen-empty-table-message
                                    v-if="grants.length === 0"
                                    compact
                                    icon="ZenIconShieldLock"
                                >
                                    No permissions assigned. Add a permission to
                                    control what this team can access.
                                </zen-empty-table-message>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </section>

        <add-team-permission-slide-in
            :is-open="grantModal.show"
            v-model:data="grantModal.data"
            :error="grantModal.error"
            :processing="grantModal.processing"
            :role-description="roleDescription"
            :can-submit="canSubmitGrant"
            :projects="projects"
            :selected-project-environments="selectedProjectEnvironments"
            @close="closeGrantModal"
            @confirm="submitGrant()"
        />

        <revoke-team-permission-modal
            :is-open="revokeModal.show"
            :grant="revokeModal.grant"
            :processing="revokeModal.processing"
            :scope-label-text="revokeScopeLabelText"
            @close="closeRevokeModal"
            @confirm="confirmRevoke"
        />

        <edit-team-slide-in
            :is-open="editModal.show"
            v-model:data="editModal.data"
            :error="editModal.error"
            :processing="editModal.processing"
            @close="closeEditModal"
            @confirm="submitEdit"
        />
    </main>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import AdminHeaderTabs from '@/screens/_components/admin/HeaderTabs.vue'
import AddTeamPermissionSlideIn from '@/screens/trusted/admin/modals/AddTeamPermissionSlideIn.vue'
import RevokeTeamPermissionModal from '@/screens/trusted/admin/modals/RevokeTeamPermissionModal.vue'
import EditTeamSlideIn from '@/screens/trusted/admin/modals/EditTeamSlideIn.vue'
import TeamService from '@/services/team.service'
import UserService from '@/services/user.service'
import { useSessionStore } from '@/stores/session'
import { errorMessage } from '@/utils/notifications'
import { removeBy, upsertBy } from '@/utils/list-mutations'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'

const route = useRoute()
const sessionStore = useSessionStore()
const { can, isAdmin } = usePermissions()

// State
const loading = ref(false)
const team = ref(null)
const members = ref([])
const grants = ref([])
const selectedUserUlid = ref('')
const addingUser = ref(false)

const projects = computed(() => sessionStore.data?.projects?.data ?? [])
const canManageMembers = computed(() => can(PERMISSIONS.TEAM_UPDATE))

const grantModal = reactive({
    show: false,
    processing: false,
    error: null,
    data: {
        role: '',
        scope_type: '',
        selectedProjectUlid: '',
        selectedEnvironmentUlid: '',
    },
})

const revokeModal = reactive({
    show: false,
    processing: false,
    grant: null,
})

const editModal = reactive({
    show: false,
    processing: false,
    error: null,
    data: {
        name: '',
        description: '',
    },
})

// Computed
const selectedProjectEnvironments = computed(() => {
    if (!grantModal.data.selectedProjectUlid) return []
    const project = projects.value.find(
        (p) => p.ulid === grantModal.data.selectedProjectUlid
    )
    return project?.environments?.data ?? []
})

const roleDescription = computed(() => {
    switch (grantModal.data.role) {
        case 'admin':
            return 'Full access to all features and settings, including user management and billing.'
        case 'editor':
            return 'Can create, update, and delete projects, flags, targets, segments, and webhooks.'
        case 'viewer':
            return 'Read-only access to projects, flags, targets, and segments.'
        default:
            return ''
    }
})

const canSubmitGrant = computed(() => {
    if (!grantModal.data.role || !grantModal.data.scope_type) return false
    if (
        grantModal.data.role === 'admin' &&
        grantModal.data.scope_type === 'environment'
    )
        return false
    if (
        grantModal.data.scope_type === 'project' &&
        !grantModal.data.selectedProjectUlid
    )
        return false
    if (grantModal.data.scope_type === 'environment') {
        if (
            !grantModal.data.selectedProjectUlid ||
            !grantModal.data.selectedEnvironmentUlid
        )
            return false
    }
    return true
})

// Scope label resolver
function scopeLabel(grant) {
    if (!grant) return ''
    if (grant.scope_type === 'account') return 'Account'
    if (grant.scope_type === 'project') {
        const project = projects.value.find((p) => p.ulid === grant.scope_ulid)
        return project ? project.name : 'Project'
    }
    if (grant.scope_type === 'environment') {
        for (const project of projects.value) {
            const env = project.environments?.data?.find(
                (e) => e.ulid === grant.scope_ulid
            )
            if (env) return `${project.name} / ${env.name}`
        }
        return 'Environment'
    }
    return grant.scope_type
}

// Label for the grant currently targeted by the revoke modal, computed here
// so the modal receives a plain string instead of calling scopeLabel(grant)
// itself (grant can be null before selection or during the close transition).
const revokeScopeLabelText = computed(() => scopeLabel(revokeModal.grant))

// Data loading
async function fetchTeam() {
    const teamUlid = route.params.teamUlid
    loading.value = true
    try {
        const response = await TeamService.get(teamUlid)
        team.value = response.data
        members.value = [...(response.data.users?.data ?? [])]
    } catch (error) {
        errorMessage(error)
    } finally {
        loading.value = false
    }
}

async function fetchGrants() {
    const teamUlid = route.params.teamUlid
    try {
        const response = await TeamService.listGrants(teamUlid)
        grants.value = response.data ?? []
    } catch (error) {
        errorMessage(error)
    }
}

async function searchAvailableUsers(query) {
    try {
        const response = await UserService.search({
            search: query,
            per_page: 20,
        })
        const memberUlids = new Set(members.value.map((m) => m.ulid))
        return (response.data ?? [])
            .filter((user) => !memberUlids.has(user.ulid))
            .map((user) => ({
                ulid: user.ulid,
                label: `${[user.first_name, user.last_name].filter(Boolean).join(' ')} (${user.email})`,
            }))
    } catch (error) {
        errorMessage(error)
        return []
    }
}

// Member management
async function addMember() {
    if (!canManageMembers.value) return
    if (!selectedUserUlid.value) return
    addingUser.value = true
    try {
        const response = await TeamService.addUser(
            route.params.teamUlid,
            selectedUserUlid.value
        )
        members.value = response.data.users?.data ?? []
        team.value = response.data
        selectedUserUlid.value = ''
    } catch (error) {
        errorMessage(error)
    } finally {
        addingUser.value = false
    }
}

async function removeMember(userUlid) {
    if (!canManageMembers.value) return
    try {
        const response = await TeamService.removeUser(
            route.params.teamUlid,
            userUlid
        )
        members.value = response.data.users?.data ?? []
        team.value = response.data
    } catch (error) {
        errorMessage(error)
    }
}

// Grant management
function openGrantModal() {
    grantModal.show = true
    grantModal.error = null
    grantModal.processing = false
    grantModal.data.role = ''
    grantModal.data.scope_type = ''
    grantModal.data.selectedProjectUlid = ''
    grantModal.data.selectedEnvironmentUlid = ''
}

function closeGrantModal() {
    grantModal.show = false
}

// When role changes, enforce admin scope constraints
watch(
    () => grantModal.data.role,
    (newRole) => {
        if (
            newRole === 'admin' &&
            grantModal.data.scope_type === 'environment'
        ) {
            grantModal.data.scope_type = 'account'
        }
    }
)

// When scope type changes, clear downstream selections
watch(
    () => grantModal.data.scope_type,
    () => {
        grantModal.data.selectedProjectUlid = ''
        grantModal.data.selectedEnvironmentUlid = ''
    }
)

// When project changes, clear environment selection
watch(
    () => grantModal.data.selectedProjectUlid,
    () => {
        grantModal.data.selectedEnvironmentUlid = ''
    }
)

async function submitGrant() {
    grantModal.processing = true
    grantModal.error = null

    const accountUlid = sessionStore.data?.account?.data?.ulid
    let scopeUlid = accountUlid

    if (grantModal.data.scope_type === 'project') {
        scopeUlid = grantModal.data.selectedProjectUlid
    } else if (grantModal.data.scope_type === 'environment') {
        scopeUlid = grantModal.data.selectedEnvironmentUlid
    }

    try {
        const response = await TeamService.createGrant(route.params.teamUlid, {
            role: grantModal.data.role,
            scope_type: grantModal.data.scope_type,
            scope_ulid: scopeUlid,
        })
        closeGrantModal()
        grants.value = upsertBy(grants.value, response.data, 'ulid')
    } catch (error) {
        grantModal.error = error
    } finally {
        grantModal.processing = false
    }
}

function revokeGrantConfirm(grant) {
    revokeModal.grant = grant
    revokeModal.show = true
    revokeModal.processing = false
}

function closeRevokeModal() {
    revokeModal.show = false
    revokeModal.grant = null
}

async function confirmRevoke() {
    revokeModal.processing = true
    try {
        const grantUlid = revokeModal.grant.ulid
        await TeamService.revokeGrant(route.params.teamUlid, grantUlid)
        closeRevokeModal()
        grants.value = removeBy(grants.value, grantUlid)
    } catch (error) {
        errorMessage(error)
    } finally {
        revokeModal.processing = false
    }
}

// Edit team
function openEditModal() {
    editModal.data.name = team.value?.name ?? ''
    editModal.data.description = team.value?.description ?? ''
    editModal.error = null
    editModal.show = true
}

function closeEditModal() {
    editModal.show = false
    editModal.error = null
}

async function submitEdit() {
    editModal.processing = true
    editModal.error = null
    try {
        const response = await TeamService.update(route.params.teamUlid, {
            name: editModal.data.name.trim(),
            description: editModal.data.description?.trim() || null,
        })
        team.value = response.data
        members.value = [...(response.data.users?.data ?? [])]
        closeEditModal()
    } catch (error) {
        editModal.error = error
    } finally {
        editModal.processing = false
    }
}

// Lifecycle
onMounted(() => {
    fetchTeam()
    fetchGrants()
})
</script>
